import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import CircularProgress from '@mui/material/CircularProgress';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./Rider.module.css";
import "../../components/mcrbTableTheme.css";

const { baseNodeapiUrl } = config;

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Strip any country code / "+91" / non-digits and keep the last 10 digits
const normalizePhone = (v: any) => {
    const digits = String(v ?? "").replace(/\D/g, "");
    return digits.length > 10 ? digits.slice(-10) : digits;
};

const toastOpts: any = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const UpdateRider = () => {
    const history = useHistory();
    const { id }: any = useParams();
    const [rider, setRider] = useState<any>({
        name: "",
        whatsapp_number: "",
        email: "",
        address: "",
        status: true,
    });
    const [fetching, setFetching] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ name: "", whatsapp_number: "", email: "" });

    const fetchRider = async () => {
        try {
            setFetching(true);
            const { data } = await axios({
                url: `${baseNodeapiUrl}/invlrider/getrider`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });
            if (data.success) {
                const found = (data.data || []).find((item: any) => String(item.id) === String(id));
                if (found) {
                    setRider({
                        name: found.name || "",
                        whatsapp_number: normalizePhone(found.whatsapp_number),
                        email: found.email || "",
                        address: found.address || "",
                        status: !!found.status,
                    });
                } else {
                    toast.error("Rider not found !", toastOpts);
                }
            }
        } catch (error) {
            toast.error("Something went wrong while fetching rider !", toastOpts);
        } finally {
            setFetching(false);
        }
    }

    useEffect(() => {
        fetchRider();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fieldChangeHandler = (e: any) => {
        const { name, value } = e.target;
        setRider((prev: any) => ({ ...prev, [name]: value }));
    }

    function fieldErrorFor(name: string, value: string) {
        if (name === "name") {
            return value ? "" : "This field is required !";
        }
        if (name === "whatsapp_number") {
            if (!value) return "This field is required !";
            if (!PHONE_REGEX.test(value)) return "Enter a valid 10-digit mobile number !";
            return "";
        }
        if (name === "email") {
            if (!value) return "";
            if (!EMAIL_REGEX.test(value)) return "Enter a valid email address !";
            return "";
        }
        return "";
    }

    function errorHandler(e: any) {
        const { name, value } = e.target;
        setErrors((prev) => ({ ...prev, [name]: fieldErrorFor(name, value) }));
    }

    function validate() {
        const nextErrors = {
            name: fieldErrorFor("name", rider.name),
            whatsapp_number: fieldErrorFor("whatsapp_number", rider.whatsapp_number),
            email: fieldErrorFor("email", rider.email),
        };
        setErrors(nextErrors);
        return !nextErrors.name && !nextErrors.whatsapp_number && !nextErrors.email;
    }

    const updateRiderHandler = async () => {
        if (!validate()) {
            toast.error("Please fix the highlighted fields !", toastOpts);
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios({
                url: `${baseNodeapiUrl}/invlrider/updaterider`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                },
                data: { rider_id: id, ...rider }
            });

            if (data.success) {
                toast.success("Rider updated successfully !", toastOpts);
                history.push('/riderlist');
            } else {
                toast.error(data.message || "Failed to update rider !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong !", toastOpts);
        } finally {
            setLoading(false);
        }
    }

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>Update Rider</span>
                    </div>
                    <div className={brandStyles.bodyArea} style={{ minHeight: "60vh" }}>
                        {
                            fetching ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Name<span className={brandStyles.required}>*</span>
                                                {errors.name ? <span className={brandStyles.errorInline}>{errors.name}</span> : null}
                                            </label>
                                            <input type="text" className={brandStyles.input} name="name" placeholder="Rider Name"
                                                value={rider.name} onChange={fieldChangeHandler} onBlur={errorHandler} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>WhatsApp Number<span className={brandStyles.required}>*</span>
                                                {errors.whatsapp_number ? <span className={brandStyles.errorInline}>{errors.whatsapp_number}</span> : null}
                                            </label>
                                            <input type="text" className={brandStyles.input} name="whatsapp_number" placeholder="10-digit mobile number"
                                                maxLength={10}
                                                inputMode="numeric"
                                                value={rider.whatsapp_number}
                                                onChange={(e) => {
                                                    setRider((prev: any) => ({ ...prev, whatsapp_number: normalizePhone(e.target.value) }));
                                                }}
                                                onBlur={errorHandler}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Email
                                                {errors.email ? <span className={brandStyles.errorInline}>{errors.email}</span> : null}
                                            </label>
                                            <input type="email" className={brandStyles.input} name="email" placeholder="rider@example.com"
                                                value={rider.email} onChange={fieldChangeHandler} onBlur={errorHandler} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Address</label>
                                            <input type="text" className={brandStyles.input} name="address" placeholder="Address"
                                                value={rider.address} onChange={fieldChangeHandler} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Status</label>
                                            <div className={brandStyles.statusRow}>
                                                <input type="checkbox" checked={rider.status} name="status"
                                                    className={brandStyles.checkbox}
                                                    onChange={(e) => setRider((prev: any) => ({ ...prev, status: !prev.status }))}
                                                />
                                                <span style={{ fontSize: "13px", color: "#6B7280", fontWeight: 600 }}>
                                                    {rider.status ? "Active" : "Inactive"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-12 mt-2">
                                        <button type="submit" className={brandStyles.updateBtn} disabled={loading} onClick={updateRiderHandler}>
                                            {loading ? "Updating..." : "Update"}
                                        </button>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </ThemeMaker>
    )
}

export default UpdateRider;
